'use client';

import { Stack, SxProps, Typography, useTheme, Button, Divider } from '@mui/material';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { TProject, TLead } from '../../types/config';
import { ContainedButton } from '../contained-button';
import { LeadFormInputs } from './lead-form-inputs';
import { PhoneButton } from './phone-button';
import { ConsentCheckbox } from './consent-checkbox';
import { messageIcon, successIcon } from '../../config/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { TErrors, LeadFormFields } from './types';
import { RTL_PHONE_LENGTH, validate } from './utils';
import { Calendar } from 'lucide-react';
import { useIsMobile } from '../../hooks';

export const formSxDefault: SxProps = {
  padding: { xs: '0', sm: '20px' },
};
const formFieldsSxDefault: SxProps = {
  gap: '10px',
};

export type LeadFormProps = {
  expanded: boolean;
  onSent?: (data: TLead) => void;
  onContinue?: () => void;
  onSubmit?: (
    event: FormEvent<HTMLFormElement>,
    name: string,
    phone: string,
    email: string,
  ) => Promise<void>;
  showAgentAvatar?: boolean;
  sx?: SxProps;
  fieldsSx?: SxProps;
  phoneButton?: boolean;
  withPromotionEvent?: boolean;
  withInventoryEvent?: boolean;
  disableAnimation?: boolean;
  title?: string;
  description?: string;
  showToast: (message: string) => void;
  t: (key: string) => string;
  project: TProject;
  onShowPhoneNumber: () => void;
  onCallSeller: () => void;
  handleWhatsappClick: () => void;
  withValidation?: boolean;
  onInit?: (setters: {
    setName: (name: string) => void;
    setPhone: (phone: string) => void;
    setEmail: (email: string) => void;
  }) => void;
  consentCheckbox?: boolean;
  consentLocation?: 'inputs' | 'bottom';
  onBookMeeting?: () => void;
  showBookMeetingButton?: boolean;
  // Field input tracking callbacks
  onFirstFieldInput?: (fieldName: LeadFormFields) => void;
  onSecondFieldInput?: (fieldName: LeadFormFields) => void;
  onThirdFieldInput?: (fieldName: LeadFormFields) => void;
};

export function LeadForm({
  expanded,
  showAgentAvatar,
  phoneButton = true,
  sx = formSxDefault,
  fieldsSx = formFieldsSxDefault,
  onSent,
  onContinue,
  onSubmit,
  withPromotionEvent = false,
  withInventoryEvent = false,
  title,
  description,
  disableAnimation,
  showToast,
  t,
  project,
  onShowPhoneNumber,
  onCallSeller,
  handleWhatsappClick,
  withValidation = true,
  onInit,
  consentCheckbox = false,
  consentLocation = 'bottom',
  onBookMeeting,
  showBookMeetingButton = false,
  onFirstFieldInput,
  onSecondFieldInput,
  onThirdFieldInput,
}: LeadFormProps) {
  const direction = useTheme().direction;
  const isMobile = useIsMobile();

  // Utility functions for field tracking
  const getFieldStates = (name: string, phone: string, email: string) => ({
    name: name.trim().length > 0,
    phone: phone.trim().length > 0,
    email: email.trim().length > 0,
  });

  const getFieldTransitions = (
    previousStates: typeof fieldStates,
    currentStates: ReturnType<typeof getFieldStates>,
  ) => ({
    nameJustFilled: !previousStates.name && currentStates.name,
    phoneJustFilled: !previousStates.phone && currentStates.phone,
    emailJustFilled: !previousStates.email && currentStates.email,
  });

  const getFilledFieldsCount = (states: typeof fieldStates) =>
    Object.values(states).filter(Boolean).length;

  const triggerFieldCallback = (
    transitions: ReturnType<typeof getFieldTransitions>,
    previousFilledCount: number,
    callbacks: {
      onFirst?: (field: LeadFormFields) => void;
      onSecond?: (field: LeadFormFields) => void;
      onThird?: (field: LeadFormFields) => void;
    },
  ) => {
    const { nameJustFilled, phoneJustFilled, emailJustFilled } = transitions;

    // Early return if no field was just filled
    if (!nameJustFilled && !phoneJustFilled && !emailJustFilled) {
      return;
    }

    // Handle first field engagement
    if (previousFilledCount === 0) {
      if (nameJustFilled) return callbacks.onFirst?.(LeadFormFields.Name);
      if (phoneJustFilled) return callbacks.onFirst?.(LeadFormFields.Phone);
      if (emailJustFilled) return callbacks.onFirst?.(LeadFormFields.Email);
      return;
    }

    // Handle second field engagement
    if (previousFilledCount === 1) {
      if (nameJustFilled) return callbacks.onSecond?.(LeadFormFields.Name);
      if (phoneJustFilled) return callbacks.onSecond?.(LeadFormFields.Phone);
      if (emailJustFilled) return callbacks.onSecond?.(LeadFormFields.Email);
      return;
    }

    // Handle third field engagement
    if (previousFilledCount === 2) {
      if (nameJustFilled) return callbacks.onThird?.(LeadFormFields.Name);
      if (phoneJustFilled) return callbacks.onThird?.(LeadFormFields.Phone);
      if (emailJustFilled) return callbacks.onThird?.(LeadFormFields.Email);
    }
  };

  const submitedRef = useRef(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [errors, setErrors] = useState<TErrors>({ name: '', phone: '', email: '', consent: '' });

  const [sent, setSent] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Field input tracking state - track individual field states
  const [fieldStates, setFieldStates] = useState({
    name: false,
    phone: false,
    email: false,
  });

  const resetForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setConsentChecked(false);
    setErrors({ name: '', phone: '', email: '', consent: '' });
    submitedRef.current = false;
    setFieldStates({ name: false, phone: false, email: false });
  };

  const handleExternalConsentValidationFailed = () => {
    // Trigger consent validation and show error
    if (consentCheckbox && !consentChecked) {
      setErrors((prev) => ({ ...prev, consent: t('info.consentRequired') }));
    }
  };
  // Clear consent error immediately when consent is checked (for external validation)
  useEffect(() => {
    if (consentCheckbox && consentChecked && errors.consent) {
      setErrors((prev) => ({ ...prev, consent: '' }));
    }
  }, [consentChecked, consentCheckbox, errors.consent]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    if (!withValidation) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    submitedRef.current = true;

    const validationErrors = validate(
      t,
      { name, phone, email, consent: consentChecked },
      direction === 'rtl',
      consentCheckbox,
    );
    setErrors(validationErrors);
    if (Object.values(validationErrors).some((error) => error)) return;

    setIsLoading(true);

    try {
      if (onSubmit) {
        await onSubmit(event, name, phone, email);
      }
      setTimeout(() => {
        setIsLoading(false);
        setSent(true);
        onSent?.({
          name,
          phone,
          email,
        });
        resetForm();
      }, 1000);

      // the StickyLeadForm component doesn't have animation
      if (disableAnimation) {
        setTimeout(() => {
          setSent(false);
        }, 2000);
      }
    } catch (error) {
      setIsLoading(false);
      showToast(t('info.failedToSendMessage'));
      console.error('Failed to send message:', error);
    }
  };

  useEffect(() => {
    if (!expanded) {
      resetForm();
    }
  }, [expanded]);

  // Call onInit with setter functions for auto-population
  useEffect(() => {
    if (onInit) {
      onInit({
        setName,
        setPhone,
        setEmail,
      });
    }
  }, [onInit]);

  useEffect(() => {
    if (submitedRef.current && withValidation) {
      setErrors(
        validate(
          t,
          { name, phone, email, consent: consentChecked },
          direction === 'rtl',
          consentCheckbox,
        ),
      );
    }
  }, [name, phone, email, consentChecked, t, direction, consentCheckbox]);

  // Field input tracking logic - track individual field state transitions
  // This captures: manual typing, paste operations (Ctrl+V/Cmd+V), browser autofill,
  // password manager autofill, and programmatic population via onInit
  useEffect(() => {
    const currentStates = getFieldStates(name, phone, email);
    const transitions = getFieldTransitions(fieldStates, currentStates);
    const previousFilledCount = getFilledFieldsCount(fieldStates);

    triggerFieldCallback(transitions, previousFilledCount, {
      onFirst: onFirstFieldInput,
      onSecond: onSecondFieldInput,
      onThird: onThirdFieldInput,
    });

    // Update field states for next comparison
    setFieldStates(currentStates);
  }, [name, phone, email, onFirstFieldInput, onSecondFieldInput, onThirdFieldInput]);

  // Additional effect to handle delayed autofill scenarios and ensure comprehensive tracking
  // Some browsers/password managers populate fields after a delay or in batches
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentStates = getFieldStates(name, phone, email);

      // Early return if no states have changed
      const statesChanged =
        currentStates.name !== fieldStates.name ||
        currentStates.phone !== fieldStates.phone ||
        currentStates.email !== fieldStates.email;

      if (!statesChanged) return;

      const transitions = getFieldTransitions(fieldStates, currentStates);
      const previousFilledCount = getFilledFieldsCount(fieldStates);

      triggerFieldCallback(transitions, previousFilledCount, {
        onFirst: onFirstFieldInput,
        onSecond: onSecondFieldInput,
        onThird: onThirdFieldInput,
      });

      // Update field states
      setFieldStates(currentStates);
    }, 100); // 100ms delay to catch delayed autofill

    return () => clearTimeout(timeoutId);
  }, [expanded, fieldStates, onFirstFieldInput, onSecondFieldInput, onThirdFieldInput]); // Trigger when form becomes visible (common autofill trigger)

  const handleRTLPhoneChange = (value: string) => {
    // leave only 10 digits
    const cleanedValue = value.replace(/[^0-9]/g, '').slice(0, RTL_PHONE_LENGTH);
    setPhone(cleanedValue);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        style={{ height: '100%' }}
        data-treedistestid="form-lead-contact"
      >
        <Stack sx={sx} height="100%" justifyContent="space-between" spacing={2}>
          <Stack position="relative" flexGrow={1}>
            {title && (
              <Stack spacing={0.5} mb={2}>
                <Typography variant="h4" color="grey.900">
                  {title}
                </Typography>
                {description && <Typography variant="body2">{description}</Typography>}
              </Stack>
            )}
            <LeadFormInputs
              t={t}
              sx={fieldsSx}
              disabled={isLoading}
              setName={setName}
              name={name}
              setPhone={setPhone}
              phone={phone}
              setEmail={setEmail}
              email={email}
              errors={errors}
              handleRTLPhoneChange={handleRTLPhoneChange}
              showConsentCheckbox={consentCheckbox && consentLocation === 'inputs'}
              consentChecked={consentChecked}
              onConsentChange={setConsentChecked}
            />

            {sent && !disableAnimation && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '15px',
                    background: '#fff',
                  }}
                >
                  {successIcon}
                  <Typography variant="h6">{t('info.messageSent')}</Typography>
                  <Typography
                    textAlign="center"
                    variant="body2"
                    sx={{ mt: '-7px', lineHeight: 1.4, letterSpacing: '0.1px' }}
                  >
                    {t('info.messageSentDescription')}
                  </Typography>
                </motion.div>
              </AnimatePresence>
            )}
          </Stack>
          <Stack sx={fieldsSx}>
            {!sent && (
              <>
                <ContainedButton
                  label={t(
                    withPromotionEvent || withInventoryEvent
                      ? 'labels.contactAgent'
                      : 'labels.submit',
                  )}
                  showLineAnimation
                  icon={showAgentAvatar ? messageIcon : undefined}
                  loading={isLoading}
                  data-treedistestid="button-lead-form-submit"
                />
              </>
            )}
            {sent && (
              <ContainedButton
                label={t('labels.continue')}
                onClick={onContinue}
                buttonSx={{ marginInlineEnd: phoneButton && !isMobile ? 1 : undefined, flex: 1 }}
                data-treedistestid="button-lead-form-continue"
              />
            )}

            {phoneButton && (
              <PhoneButton
                project={project}
                t={t}
                onShowPhoneNumber={onShowPhoneNumber}
                onCallSeller={onCallSeller}
                handleWhatsappClick={handleWhatsappClick}
                consentCheckbox={consentCheckbox}
                useExternalConsent={consentCheckbox}
                externalConsentChecked={consentChecked}
                onExternalConsentValidationFailed={handleExternalConsentValidationFailed}
                withDivider={!(showBookMeetingButton && onBookMeeting)}
              />
            )}

            {!sent && showBookMeetingButton && onBookMeeting && (
              <>
                <Divider>
                  <Typography color="grey.400" fontWeight={600}>
                    {t('labels.or')}
                  </Typography>
                </Divider>
                <Button
                  variant="outlined"
                  fullWidth
                  color="primary"
                  onClick={onBookMeeting}
                  sx={{
                    height: '44px',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    width="100%"
                  >
                    <Calendar size={18} />

                    <Typography whiteSpace="nowrap" fontWeight={600}>
                      {t('labels.bookMeeting')}
                    </Typography>
                  </Stack>
                </Button>
              </>
            )}

            {consentCheckbox && consentLocation === 'bottom' && (
              <ConsentCheckbox
                checked={consentChecked}
                onChange={setConsentChecked}
                disabled={isLoading}
                error={errors.consent}
                t={t}
              />
            )}
          </Stack>
        </Stack>
      </form>
    </>
  );
}
